import { memo } from 'react';
import { SVGIcon } from '../SVGIcon';
import { BadgeStatusType } from '../Badge/BadgeTypes';
import type { TireData } from './TiresControl.types';
import { Badge } from '../Badge/Badge';
import { TireIconWithStatus } from './TireIconWithStatus';

interface TireCardProps {
  tire: TireData;
  isSelected: boolean;
  onClick: () => void;
}

export const TireCard = memo(({ tire, onClick }: TireCardProps) => {
  return (
    <div
      className="flex relative flex-col w-full h-auto transition-all duration-200 cursor-pointer hover:shadow-lg"
      onClick={onClick}
    >
      <div className="flex flex-col h-full">
        {/* Header z ikoną i nazwą */}
        <div className="border-b border-grayBorder px-[17px] py-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex gap-3 items-center">
              <TireIconWithStatus tire={tire} type="tire" />
              <div>
                <h3 className="font-archivo text-[20px] font-bold leading-4 tracking-[0.2px] text-text-primary" style={{ fontVariationSettings: "'wdth' 100" }}>
                  Opona
                </h3>
              </div>
            </div>
            {/* Badge "Do wymiany" */}
            {tire.replacementStatus && (
              <div className="flex flex-col gap-2 justify-end">
                <span className="font-inter text-[12px] font-normal leading-4 text-text-primary">
                  Do wymiany:
                </span>
                <Badge
                  variant={tire.replacementStatus === 'Tak' ? BadgeStatusType.Error : tire.replacementStatus === 'Nie' ? BadgeStatusType.Success : BadgeStatusType.Warning}
                  size='sm'
                  version='v1'
                  badgeText={tire.replacementStatus}
                />
              </div>
            )}
          </div>

          {/* Marka, model i rozmiar */}
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <div className="min-w-0">
              <h4 className="font-archivo text-[17px] font-semibold leading-[18px] text-text-primary" style={{ fontVariationSettings: "'wdth' 100" }}>
                {tire.brand}
              </h4>
              <p className="font-archivo text-[13px] font-normal leading-[15px] tracking-[0.3px] text-text-subtle" style={{ fontVariationSettings: "'wdth' 100" }}>
                {tire.model}
              </p>
            </div>
            <Badge variant={BadgeStatusType.Neutral} size="sm" version="v2" badgeText={tire.size} classNames='text-grayText mt-auto' />
          </div>
        </div>

        {/* Szczegóły opony */}
        <div className="flex-1 px-[17px] py-4">
          <div className="space-y-[18px]">
            {/* Rodzaj opony */}
            <div className="flex gap-3 items-center">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
                <SVGIcon name="location-pin-map" size={20} fill="grayStale" />
              </div>
              <div>
                <p className="font-inter text-[13px] font-normal leading-[14px] text-grayText">
                  Rodzaj opony
                </p>
                <p className="font-inter text-[14px] font-semibold leading-4 text-text-primary">
                  {tire.type}
                </p>
              </div>
            </div>

            {/* Kod daty produkcji */}
            <div className="flex gap-3 items-center">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
                <SVGIcon name="calendar-date" size={20} fill="grayStale" />
              </div>
              <div>
                <p className="font-inter text-[13px] font-normal leading-[14px] text-grayText">
                  Kod daty produkcji
                </p>
                <p className="font-inter text-[14px] font-semibold leading-4 text-text-primary">
                  {tire.productionDate}
                </p>
              </div>
            </div>

            {/* Indeks prędkości */}
            <div className="flex gap-3 items-center">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
                <SVGIcon name="media-play" size={20} fill="grayStale" />
              </div>
              <div>
                <p className="font-inter text-[13px] font-normal leading-[14px] text-grayText">
                  Indeks prędkości
                </p>
                <p className="font-inter text-[14px] font-semibold leading-4 text-text-primary">
                  {tire.speedIndex}
                </p>
              </div>
            </div>

            {/* Bieżnik */}
            <div>
              <div className="flex gap-3 items-center">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
                  <SVGIcon name="server-stack" size={20} fill="grayStale" />
                </div>
                <p className="font-inter text-[14px] font-semibold leading-4 text-text-primary">
                  Bieżnik [mm]:
                </p>
              </div>
              <div className="flex justify-between gap-4 mt-2">
                <div className='flex flex-col align-center text-center'>
                  <span className="font-inter text-[13px] font-normal text-grayText">Str. wewn.: </span>
                  <span className="font-inter text-[14px] font-semibold text-text-primary">{tire.treadDepth.inner}</span>
                </div>
                <div className='flex flex-col align-center text-center'>
                  <span className="font-inter text-[13px] font-normal text-grayText">Środek: </span>
                  <span className="font-inter text-[14px] font-semibold text-text-primary">{tire.treadDepth.center}</span>
                </div>
                <div className='flex flex-col align-center text-center'>
                  <span className="font-inter text-[13px] font-normal text-grayText">Str. zewn: </span>
                  <span className="font-inter text-[14px] font-semibold text-text-primary">{tire.treadDepth.outer}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Felga */}
        <div className="border-t border-grayBorder pl-[17px] lg:pl-[10px] xl:pl-[17px] lg:pr-[15px] xl:pr-[20px] py-[30px]">
          <div className="flex flex-wrap xl:flex-nowrap gap-3 xl:items-start">
            <div className="flex gap-3 items-center">
              <TireIconWithStatus tire={tire} type="rim" />
              <h3 className="font-archivo text-[20px] font-bold leading-4 tracking-[0.2px] text-text-primary" style={{ fontVariationSettings: "'wdth' 100" }}>
                Felga
              </h3>
            </div>
            <div className="flex flex-col gap-1 xl:ml-auto">
              <div className="flex items-baseline">
                <span className="font-inter text-[13px] font-normal text-grayText w-[55px]">
                  Rodzaj:
                </span>
                <span className="font-inter text-[14px] font-semibold text-text-primary">
                  {tire.rimType}
                </span>
              </div>
              <div className="flex items-baseline">
                <span className="font-inter text-[13px] font-normal text-grayText w-[55px]">
                  Stan:
                </span>
                <span className="font-inter text-[14px] font-semibold text-text-primary">
                  {tire.rimStatus}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
